import { supabase } from "../../integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type {
  GetEntityDataResponse,
  UpdateEntityDataRequest,
  UpdateEntityDataResponse,
} from "../types/entityData";

/**
 * Get entity data for a given session
 *
 * @param sessionId - The form session ID
 * @returns Promise with entity data (null if not found)
 * @throws {Error} If the response format is invalid or sessionId is missing
 *
 * @example
 * const data = await getEntityData("session-123");
 * if (data.entity_data) {
 *   console.log("Impact analysis:", data.entity_data.impact_analysis);
 * }
 */
export async function getEntityData(
  sessionId: string
): Promise<GetEntityDataResponse> {
  if (!sessionId) {
    throw new Error("session_id is required");
  }

  const { data, error } = await supabase.functions.invoke(
    `api/entity-data?session_id=${encodeURIComponent(sessionId)}`,
    {
      method: "GET",
    }
  );

  // Handle errors (network issues, 500s, etc.)
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (data === undefined || data === null) {
    throw new Error("Invalid response format from entity-data endpoint");
  }

  return data;
}

export async function updateEntityData(
  request: UpdateEntityDataRequest
): Promise<UpdateEntityDataResponse> {
  if (!request.session_id) {
    throw new Error("session_id is required");
  }

  if (!request.updates || Object.keys(request.updates).length === 0) {
    throw new Error("updates object is required and must not be empty");
  }

  const { data, error } = await supabase.functions.invoke("api/entity-data", {
    method: "PATCH",
    body: request,
  });

  await handleSupabaseFunctionError(error);

  if (!data || typeof data.success !== "boolean") {
    throw new Error("Invalid response format from entity-data endpoint");
  }

  return data;
}
